import React, { createContext, useContext, useReducer, useCallback, useMemo } from "react";
import api from "/imports/api/client";
import { applyBranding } from "/imports/libs/branding";
import { track } from "/imports/libs/analytics";

/**
 * Initial state
 */
const initialState = {
  // Widget config
  slug: null,
  widget: null,
  saasId: null,
  // Club profile subset (name, website/social links, whatsappNumber) — used
  // by the footer and anywhere else club identity/contact is shown.
  saas: null,
  currency: "$",

  // Web push for this club: { enabled, publicKey }. Null or disabled means no
  // push UI anywhere.
  push: null,

  // Cancellation policies used for the checkout policy copy. `policies` is the
  // club default; `locationPolicies` holds per-location override groups.
  policies: [],
  locationPolicies: [],

  // Configuration data
  locations: [],
  services: [],
  trainers: [],
  facilities: [],
  sports: [],
  vouchers: [],

  // Selected filters
  selectedLocation: null,

  // User's eligibility (levels per sport)
  eligibility: {},

  // Cart / booking selection
  cart: {
    type: null, // 'court' | 'lesson' | 'class' | 'term'
    items: []
  },

  // Loading states
  loading: false,
  error: null,
  initialized: false
};

/**
 * Action types
 */
const ActionTypes = {
  INIT_START: "INIT_START",
  INIT_SUCCESS: "INIT_SUCCESS",
  INIT_ERROR: "INIT_ERROR",
  SET_LOCATION: "SET_LOCATION",
  SET_CART: "SET_CART",
  CLEAR_CART: "CLEAR_CART",
  ADD_TO_CART: "ADD_TO_CART",
  REMOVE_FROM_CART: "REMOVE_FROM_CART",
  UPDATE_ELIGIBILITY: "UPDATE_ELIGIBILITY",
  RESET: "RESET"
};

/**
 * Reducer
 */
function widgetReducer(state, action) {
  switch (action.type) {
    case ActionTypes.INIT_START:
      return {
        ...state,
        loading: true,
        error: null
      };

    case ActionTypes.INIT_SUCCESS:
      return {
        ...state,
        loading: false,
        initialized: true,
        slug: action.payload.slug,
        widget: action.payload.widget,
        saasId: action.payload.saasId,
        saas: action.payload.saas || null,
        push: action.payload.push || null,
        currency: action.payload.currency || "$",
        policies: action.payload.policies || [],
        locationPolicies: action.payload.locationPolicies || [],
        locations: action.payload.locations || [],
        services: action.payload.services || [],
        trainers: action.payload.trainers || [],
        facilities: action.payload.facilities || [],
        sports: action.payload.sports || [],
        vouchers: action.payload.vouchers || [],
        selectedLocation: action.payload.defaultLocation || null,
        eligibility: action.payload.eligibility || {}
      };

    case ActionTypes.INIT_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
        initialized: false
      };

    case ActionTypes.SET_LOCATION:
      return {
        ...state,
        selectedLocation: action.payload
      };

    case ActionTypes.SET_CART:
      return {
        ...state,
        cart: action.payload
      };

    case ActionTypes.ADD_TO_CART:
      // v2 books a single bookish per invoice. A multi-booking invoice can't be
      // partially cancelled (neither the widget nor partners supports cancelling
      // one booking out of several), so a new selection replaces the cart
      // instead of appending — the cart is capped at one item.
      return {
        ...state,
        cart: {
          type: action.payload.type,
          items: [action.payload.item]
        }
      };

    case ActionTypes.REMOVE_FROM_CART:
      return {
        ...state,
        cart: {
          ...state.cart,
          items: state.cart.items.filter((_, idx) => idx !== action.payload)
        }
      };

    case ActionTypes.CLEAR_CART:
      return {
        ...state,
        cart: { type: null, items: [] }
      };

    case ActionTypes.UPDATE_ELIGIBILITY:
      return {
        ...state,
        eligibility: {
          ...state.eligibility,
          ...action.payload
        }
      };

    case ActionTypes.RESET:
      return initialState;

    default:
      return state;
  }
}

/**
 * Context
 */
const WidgetContext = createContext(null);

/**
 * Provider component
 */
export function WidgetProvider({ children }) {
  const [state, dispatch] = useReducer(widgetReducer, initialState);

  /**
   * Initialize widget by slug
   */
  const initializeWidget = useCallback(async (slug, locationId = null) => {
    dispatch({ type: ActionTypes.INIT_START });

    try {
      const data = await api.initWidget(slug, locationId);

      applyBranding({ name: data?.saas?.name, logoUrl: data?.saas?.logoUrl, slug, color: data?.widget?.color });

      dispatch({
        type: ActionTypes.INIT_SUCCESS,
        payload: {
          slug,
          widget: { ...data.widget, logoUrl: data?.saas?.logoUrl || "" },
          saasId: data.saasId,
          saas: data.saas,
          push: data.push,
          currency: data.currency,
          policies: data?.saas?.policies || [],
          locationPolicies: data?.saas?.locationPolicies || [],
          locations: data.locations,
          services: data.services,
          trainers: data.trainers,
          facilities: data.facilities,
          sports: data.sports,
          vouchers: data.vouchers,
          defaultLocation: data.defaultLocation,
          eligibility: data.eligibility
        }
      });

      return data;
    } catch (err) {
      dispatch({
        type: ActionTypes.INIT_ERROR,
        payload: err.message || "Failed to initialize widget"
      });
      throw err;
    }
  }, []);

  /**
   * Set selected location
   */
  const setLocation = useCallback((location) => {
    dispatch({ type: ActionTypes.SET_LOCATION, payload: location });
  }, []);

  /**
   * Add item to cart
   */
  const addToCart = useCallback((type, item) => {
    dispatch({
      type: ActionTypes.ADD_TO_CART,
      payload: { type, item }
    });
    // Every booking surface (courts, lessons, classes, events, recommendations)
    // picks its slot through here. Signed-out picks go to the sign-in wall next.
    track("slot_selected", {
      kind: type,
      service_id: item?.serviceId || null,
      service_name: item?.serviceName || item?.name || null,
      location_id: item?.locationId || null,
      trainer_id: item?.trainerId || null,
      class_id: item?.bookishId || null,
      date: item?.date || null,
      time: item?.time || null,
      duration: item?.duration || null,
      price: item?.price ?? null,
      currency: item?.currency || null,
      any_court: !!item?.facilityAuto,
      signed_in: api.isLoggedIn()
    });
  }, []);

  /**
   * Remove item from cart by index
   */
  const removeFromCart = useCallback((index) => {
    dispatch({ type: ActionTypes.REMOVE_FROM_CART, payload: index });
  }, []);

  /**
   * Clear cart
   */
  const clearCart = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_CART });
  }, []);

  /**
   * Set entire cart
   */
  const setCart = useCallback((cart) => {
    dispatch({ type: ActionTypes.SET_CART, payload: cart });
  }, []);

  /**
   * Update eligibility after assessment
   */
  const updateEligibility = useCallback((eligibility) => {
    dispatch({ type: ActionTypes.UPDATE_ELIGIBILITY, payload: eligibility });
  }, []);

  /**
   * Reset widget state
   */
  const reset = useCallback(() => {
    dispatch({ type: ActionTypes.RESET });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      initializeWidget,
      setLocation,
      addToCart,
      removeFromCart,
      clearCart,
      setCart,
      updateEligibility,
      reset
    }),
    [state, initializeWidget, setLocation, addToCart, removeFromCart, clearCart, setCart, updateEligibility, reset]
  );

  return <WidgetContext.Provider value={value}>{children}</WidgetContext.Provider>;
}

/**
 * Hook to use widget context
 */
export function useWidget() {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error("useWidget must be used within a WidgetProvider");
  }
  return context;
}

export default WidgetContext;
